import React, { useState, useEffect } from 'react';
import { TeamService, Team } from '../../services/team.service';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import { PlusIcon, ArrowPathIcon, ChevronLeftIcon, UsersIcon, ShieldCheckIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import TeamCard from '../../components/team/TeamCard';
import CreateTeamModal from '../../components/team/CreateTeamModal';
import { useAppSelector } from '../../hooks/useRedux';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import UpgradePromptModal from '../../components/common/UpgradePromptModal';
import { hasPermission } from '../../utils/permissionUtils';
import HeroBanner from '../../components/common/HeroBanner';

// Extend the Team type to include departments
interface TeamWithDepartments extends Team {
  departments?: Team[];
}

// Define tabs for the page
type TabType = 'teams' | 'members' | 'roles';

const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [teams, setTeams] = useState<TeamWithDepartments[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { permissions, role_id, team_id, user } = useAppSelector(state => state.auth);
  const canManageTeams = hasPermission(permissions, 'team', 'create_teams');
  const [teamStorageData, setTeamStorageData] = useState<{
    [key: string]: {
      used: number;
      total: number;
      percentage: number;
      items: number;
    };
  }>({});
  const [refreshingTeam, setRefreshingTeam] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // New state for tracking selected organization and viewing departments
  const [selectedOrg, setSelectedOrg] = useState<TeamWithDepartments | null>(null);
  const [isDepartmentView, setIsDepartmentView] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('teams');

  // Check if user is app admin (role_id = 1)
  const isAppAdmin = role_id === 1;

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await TeamService.getUserTeams();

      if (isAppAdmin) {
        // App admin - filter to only get parent organizations
        const organizations = response.teams?.filter(team =>
          team.team_type === 'organization' || !team.parent_team_id
        ) || [];
        setTeams(organizations);
      } else if (team_id) {
        // Other admin - filter to only get departments for their team
        const departments = response.teams?.filter(team =>
          team.parent_team_id === team_id
        ) || [];
        setTeams(departments);
      } else {
        setTeams(response.teams || []);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (organization: TeamWithDepartments) => {
    try {
      setLoading(true);
      const response = await TeamService.getUserTeams();

      // Filter to get departments for this organization
      const departments = response.teams?.filter(team =>
        team.parent_team_id === organization.id
      ) || [];

      // Set the selected organization with its departments
      const updatedOrg = { ...organization, departments };
      setSelectedOrg(updatedOrg);
      setIsDepartmentView(true);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, [isAppAdmin, team_id]);

  const handleTeamCreated = () => {
    if (isDepartmentView && selectedOrg) {
      fetchDepartments(selectedOrg);
    } else {
      fetchTeams();
    }
    setIsModalOpen(false);
  };

  const handleTeamDeleted = (teamId: string) => {
    if (isDepartmentView && selectedOrg) {
      // Remove from departments if we're in department view
      const updatedDepartments = selectedOrg.departments?.filter(dept => dept.id !== teamId) || [];
      setSelectedOrg({ ...selectedOrg, departments: updatedDepartments });
    } else {
      // Remove from top-level teams
      setTeams(teams.filter(team => team.id !== teamId));
    }
  };

  const handleRefreshTeamStorage = async (teamId: string) => {
    try {
      setRefreshingTeam(teamId);
      const response = await TeamService.fetchTeamStorageUsage(teamId);
      
      if (response.success && response.storageData) {
        // Format the storage data properly
        setTeamStorageData(prev => ({
          ...prev,
          [teamId]: {
            used: response.storageData.totalBytes || 0,
            total: response.storageData.limit || 52428800, // Default to 50MB if not provided
            percentage: response.storageData.usagePercentage || 0,
            items: response.storageData.totalItems || 0
          }
        }));
      }

      if (isDepartmentView && selectedOrg) {
        fetchDepartments(selectedOrg);
      } else {
        fetchTeams();
      }
      toast.success('Storage usage updated');
    } catch (error) {
      console.error('Error fetching team storage:', error);
      toast.error('Failed to refresh storage data');
    } finally {
      setRefreshingTeam(null);
    }
  };

  const viewDepartments = (organization: TeamWithDepartments) => {
    fetchDepartments(organization);
  };

  const backToOrganizations = () => {
    setSelectedOrg(null);
    setIsDepartmentView(false);
    fetchTeams();
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === 'members') {
      navigate('/manage-members');
    } else if (tab === 'roles') {
      navigate('/roles');
    }
  };

  // If the current URL is /manage-members or /roles, show the appropriate tab
  useEffect(() => {
    if (location.pathname === '/manage-members') {
      setActiveTab('members');
    } else if (location.pathname === '/roles') {
      setActiveTab('roles');
    } else {
      setActiveTab('teams');
    }
  }, [location.pathname]);

  // Render the teams view
  const renderTeamsView = () => {
    if (loading) {
      return (
        <div className="mt-6 flex justify-center">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-6 py-1">
              <div className="h-40 bg-gray-200 rounded"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        {isAppAdmin && isDepartmentView && selectedOrg ? (
          // Department view for app admin
          <>
            {selectedOrg.departments && selectedOrg.departments.length > 0 ? (
              <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {selectedOrg.departments.map((department) => (
                  <TeamCard
                    key={department.id}
                    team={department}
                    onDeleted={() => handleTeamDeleted(department.id)}
                    onUpdated={() => fetchDepartments(selectedOrg)}
                    onRefresh={() => handleRefreshTeamStorage(department.id)}
                    storageData={teamStorageData[department.id]}
                    isRefreshing={refreshingTeam === department.id}
                    isDepartment={true}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 px-6">
                <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                          rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <UserGroupIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No teams found in {selectedOrg.name}</h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                  Get started by creating your first team to organize and collaborate with your colleagues.
                </p>
                {canManageTeams && (
                  <button
                    className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Create your first team
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          teams.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                        rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <UserGroupIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No teams found</h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                Get started by creating your first team to organize and collaborate with your colleagues.
              </p>
              {canManageTeams && (
                <button
                  className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                  onClick={() => setIsModalOpen(true)}
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Create your first team
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">{teams.map((team) => (
                <TeamCard
                  key={team.id}
                  team={team}
                  onDeleted={() => handleTeamDeleted(team.id)}
                  onUpdated={fetchTeams}
                  onRefresh={() => handleRefreshTeamStorage(team.id)}
                  storageData={teamStorageData[team.id]}
                  isRefreshing={refreshingTeam === team.id}
                  // Only show "View Departments" for organizations when user is app admin
                  onViewDepartments={isAppAdmin && (!team.team_type || team.team_type === 'organization') ?
                    () => viewDepartments(team) : undefined}
                  isDepartment={team.team_type === 'department'}
                  isOrganization={!team.team_type || team.team_type === 'organization'}
                />
              ))}
            </div>
          )
        )}
      </>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      <VaultSidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <VaultHeader />
        <main className="flex-1 relative overflow-y-auto focus:outline-none bg-gray-50 dark:bg-gray-900">
          {/* Hero Banner */}
          <HeroBanner
            title={isAppAdmin && isDepartmentView && selectedOrg
              ? `${selectedOrg.name} - Departments`
              : 'Team Management'
            }
            description={isAppAdmin && isDepartmentView && selectedOrg
              ? 'Manage departments within your organization'
              : 'Organize and manage your teams effectively'
            }
            icon={<UserGroupIcon className="w-8 h-8 text-white" />}
            variant="teams"
            backButton={isAppAdmin && isDepartmentView && selectedOrg ? {
              onClick: backToOrganizations,
              icon: <ChevronLeftIcon className="h-6 w-6 text-white" />
            } : undefined}
            rightContent={hasPermission(permissions, 'team', 'create_teams') && role_id !== 1 ? (
              <>
                <button
                  onClick={() => navigate(`/team/${team_id}/transactions`)}
                  className="px-4 py-2.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  View Transactions
                </button>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="px-4 py-2.5 bg-white text-primary-600 hover:bg-primary-50 rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Upgrade Now
                </button>
              </>
            ) : undefined}
          >
            {/* Tab Navigation */}
            <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-2xl p-1 sm:p-1.5 shadow-lg border border-white/30 overflow-x-auto">
              {hasPermission(permissions, 'team', 'view_teams') && (
                <button
                  onClick={() => handleTabChange('teams')}
                  className={`flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${activeTab === 'teams'
                      ? 'bg-white text-primary-600 shadow-lg'
                      : 'text-white/80 hover:text-white hover:bg-white/20'
                    }`}
                >
                  <UserGroupIcon className="h-5 w-5 sm:mr-2" />
                  <span className="hidden sm:inline">Teams</span>
                </button>
              )}
              {hasPermission(permissions, 'team', 'view_team_members') && (
                <button
                  onClick={() => handleTabChange('members')}
                  className={`flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${activeTab === 'members'
                      ? 'bg-white text-primary-600 shadow-lg'
                      : 'text-white/80 hover:text-white hover:bg-white/20'
                    }`}
                >
                  <UsersIcon className="h-5 w-5 sm:mr-2" />
                  <span className="hidden sm:inline">Team Members</span>
                </button>
              )}
              {hasPermission(permissions, 'role_management', 'view_roles') && (
                <button
                  onClick={() => handleTabChange('roles')}
                  className={`flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${activeTab === 'roles'
                      ? 'bg-white text-primary-600 shadow-lg'
                      : 'text-white/80 hover:text-white hover:bg-white/20'
                    }`}
                >
                  <ShieldCheckIcon className="h-5 w-5 sm:mr-2" />
                  <span className="hidden sm:inline">Roles</span>
                </button>
              )}
            </div>
          </HeroBanner>

          {/* Content Section */}
          <div className="px-6 py-8">
            <div className="w-full">
              {/* Content for the selected tab */}
              {activeTab === 'teams' && renderTeamsView()}
              {(activeTab === 'members' || activeTab === 'roles') && (
                <div className="redirect-loader flex justify-center items-center p-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>
          </div>

          {/* Floating Action Button */}
          {activeTab === 'teams' && hasPermission(permissions, 'team', 'create_teams') && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-primary-600 hover:bg-primary-700 text-white rounded-full p-3 sm:p-4 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center justify-center z-10"
              aria-label="Create Team"
            >
              <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              <span className="ml-2 hidden sm:inline">Create Team</span>
            </button>
          )}
        </main>
      </div>

      <CreateTeamModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTeamCreated={handleTeamCreated}
        isDepartment={isAppAdmin && isDepartmentView}
        parentTeamId={isAppAdmin && isDepartmentView && selectedOrg ? selectedOrg.id : undefined}
      />

      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          navigate(`/upgrade?teamId=${team_id}`);
        }}
        teamId={team_id || ''}
      />
    </div>
  );
};

export default TeamsPage;